import {useMutation, useQuery} from '@apollo/client'
import {FormEvent, useEffect, useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {EDIT_POLICY} from '../../../gql/mutations/policyMutation'
import Select from 'react-select'
import {parseId} from '../../../helpers'
import {IResUpdatePolicyApi} from '../../../types/policies'
import {GET_ALL_LEAD_SOURCES} from '../../../gql/queries/leadSourceQuery'
import {IResLeadSource} from '../../leadSource/leadSourceTypes'

interface IModalProps {
  id: string
  isOpen: boolean
  onClose: () => void
  refetch: (vars?: any) => any
  currentSourceId?: string
  currentSourceName?: string
}

export const ModalChangeSourcePolicy: React.FC<IModalProps> = ({
  id,
  isOpen,
  onClose,
  refetch,
  currentSourceId,
  currentSourceName,
}) => {
  const {data: dataLeadSources, loading: loadingLeadSources} = useQuery<IResLeadSource>(
    GET_ALL_LEAD_SOURCES,
    {
      variables: {category: 'Policy', enabled: true},
      fetchPolicy: 'network-only',
    }
  )

  const [gqlUpdatePolicy, {data: dataUpdate, loading, error: errorUpdate}] =
    useMutation<IResUpdatePolicyApi>(EDIT_POLICY)

  const [leadSourceId, setLeadSourceId] = useState<string | null>(null)
  const [selectedValue, setSelectedValue] = useState<{label: string; value: string} | null>(null)
  const [options, setOptions] = useState<{label: string; value: string}[]>([])

  useEffect(() => {
    if (!dataLeadSources) return
    const opts = dataLeadSources.allLeadSources.edges.map(({node}) => ({
      label: node.name,
      value: node.id,
    }))
    setOptions(opts)

    // Set current source as default
    if (currentSourceId) {
      setLeadSourceId(parseId(currentSourceId).toString())
      const current = opts.find((opt) => opt.value === currentSourceId)
      if (current) {
        setSelectedValue(current)
      }
    }
  }, [dataLeadSources, currentSourceId])

  useEffect(() => {
    if (!dataUpdate) return

    if (dataUpdate && dataUpdate.updatePolicy.success) {
      toast.success('Source updated successfully')
      refetch({id})
      onClose()
    } else if (dataUpdate.updatePolicy.errors && dataUpdate.updatePolicy.errors.length > 0) {
      toast.error(`Error ${dataUpdate.updatePolicy.errors[0].message}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdate])

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!leadSourceId) {
      toast.error('Please select a lead source')
      return
    }

    gqlUpdatePolicy({
      variables: {
        id: parseId(id),
        policyData: {
          leadSource: leadSourceId,
        },
      },
    })
  }

  useEffect(() => {
    if (errorUpdate) {
      toast.error(`Error updating policy: ${errorUpdate.message}`)
    }
  }, [errorUpdate])

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Change Lead Source</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form id='formChangeSource' onSubmit={handleSubmit}>
          <Form.Group className='mb-3'>
            <Form.Label>Select the lead source for this policy</Form.Label>
            <Select
              isLoading={loadingLeadSources}
              loadingMessage={() => 'Loading lead sources...'}
              placeholder='Select lead source...'
              options={options}
              value={selectedValue}
              onChange={(selectedOption) => {
                if (selectedOption) {
                  setSelectedValue(selectedOption)
                  const sourceId = selectedOption.value
                  setLeadSourceId('' + parseId(sourceId))
                }
              }}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button size='sm' variant='secondary' onClick={onClose}>
          Cancel
        </Button>
        <Button
          size='sm'
          variant='primary'
          type='submit'
          form='formChangeSource'
          formAction='formChangeSource'
          disabled={!leadSourceId || loading}
        >
          {loading && <Spinner animation='border' size='sm' className='me-2' />}
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
